import { useState, useEffect } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { LinkCard } from "@/components/LinkCard";
import type { UrlResource } from "@/types/cms";

const CATEGORY_ORDER = ["training-posts", "supervisors", "registrars", "policies", "reference", "wellbeing"];

function getCategoryLabel(category: string): string {
  switch (category) {
    case "training-posts":
      return "Training Posts";
    case "supervisors":
      return "Supervisors";
    case "registrars":
      return "Registrars";
    case "policies":
      return "Policies & Requirements";
    case "reference":
      return "Reference & Guides";
    case "wellbeing":
      return "Wellbeing";
    default:
      return category;
  }
}

export function ExternalResources() {
  const [resources, setResources] = useState<UrlResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!supabase) { setLoading(false); return; }
      const { data, error } = await supabase
        .from("url_resources")
        .select("*")
        .eq("is_active", true)
        .order("category")
        .order("title");
      if (!error && data) setResources(data);
      setLoading(false);
    }
    load();
  }, []);

  if (loading || resources.length === 0) return null;

  const grouped = CATEGORY_ORDER
    .map(cat => ({
      category: cat,
      label: getCategoryLabel(cat),
      items: resources.filter(r => r.category === cat),
    }))
    .filter(g => g.items.length > 0);

  // Also include any categories not in CATEGORY_ORDER
  const knownCats = new Set(CATEGORY_ORDER);
  const extraCats = Array.from(new Set(resources.map(r => r.category).filter(c => !knownCats.has(c))));
  for (const cat of extraCats) {
    grouped.push({
      category: cat,
      label: getCategoryLabel(cat),
      items: resources.filter(r => r.category === cat),
    });
  }

  return (
    <div className="max-w-3xl w-full mb-12">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 mb-2">
          <Globe className="h-5 w-5 text-[var(--color-acrrm-orange)]" />
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">
            ACRRM Resources
          </h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Key pages from the ACRRM website — training post requirements, supervisor guides, registrar support, and more.
        </p>
      </div>

      <div className="space-y-2">
        {grouped.map(({ category, label, items }) => {
          const isExpanded = expandedCategory === category;
          return (
            <div key={category} className="rounded-xl border bg-card overflow-hidden">
              <button
                onClick={() => setExpandedCategory(isExpanded ? null : category)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-semibold text-sm text-foreground">
                    {label}
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    ({items.length})
                  </span>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`}
                />
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 grid gap-3 sm:grid-cols-2">
                  {items.map(resource => (
                    <LinkCard key={resource.id} resource={resource} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
